import { useAutoAnimate } from "@formkit/auto-animate/react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { ReactComponent as TrashIcon } from "../../../assets/icons/tables/trash-icon.svg";
import { FormSelect, PrimaryButton } from "../../../components";
import { usePartners } from "../../../store/usePartners";
import {
  IBulkDeleteResult,
  IBulkVoucherResult,
  IVoucher,
  IVoucherCounts,
  IVoucherSummary,
  bulkErrorMessage,
  useVouchers,
} from "../../../store/useVouchers";
import { withProtectedRoute } from "../../../utils/HOC/withProtectedRoute";
import { ChallengeVoucherModal } from "./ChallengeVoucherModal";
import { BulkEditVouchersModal } from "./BulkEditVouchersModal";
import { BulkDeleteVouchersModal } from "./BulkDeleteVouchersModal";
import { usePartnerChallenges } from "../../../store/usePartnerChallenges";
import { useAlert } from "../../../store/useAlert";
import { useAuth } from "../../../store/useAuth";

dayjs.extend(relativeTime);

function ChallengeVouchersScreen() {
  const navigate = useNavigate();

  const { allPartners, getPartnersForSelect } = usePartners();
  const { allPartnerChallenges, getAllPartnerChallenges } =
    usePartnerChallenges();
  const { setAlert } = useAlert();

  const {
    allVouchers,
    hasNextPage,
    getAllVouchers,
    addManyVoucher,
    deleteVoucher,
    getChallengeVoucherSelection,
    getChallengeVoucherSummary,
  } = useVouchers();
  const { user } = useAuth();
  const [animationParent] = useAutoAnimate();

  // Bulk edit and delete change a partner's whole stock at once, so only
  // ADMIN and SUPER_ADMIN get them - the same roles the backend allows.
  const canBulkEdit = [1, 7].includes(user?.user_role ?? -1);

  const [selectedPartnerId, setSelectedPartnerId] = useState<string>("");
  const [selectedChallengeId, setSelectedChallengeId] = useState<string>("");
  const [bulkPartnerId, setBulkPartnerId] = useState<string>("");
  const [bulkChallengeId, setBulkChallengeId] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
  const [summary, setSummary] = useState<IVoucherSummary | null>(null);
  const selectedPartnerIdRef = useRef(selectedPartnerId);
  selectedPartnerIdRef.current = selectedPartnerId;

  // Voucher counts of the picked partner, per challenge.
  const loadSummary = () => {
    if (!canBulkEdit || !selectedPartnerId) {
      setSummary(null);
      return;
    }
    const partnerId = selectedPartnerId;
    getChallengeVoucherSummary(partnerId)
      .then((result) => {
        // Ignore an answer for a partner that is no longer picked.
        if (partnerId === selectedPartnerIdRef.current) setSummary(result);
      })
      .catch(() => setSummary(null));
  };

  useEffect(() => {
    setSummary(null);
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPartnerId]);

  useEffect(() => {
    getPartnersForSelect();
    if (!allPartnerChallenges.length) {
      getAllPartnerChallenges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // If a partner is selected, load that partner's vouchers (of one of its
    // challenges, when one is picked); otherwise load all vouchers
    if (selectedPartnerId) {
      getAllVouchers(1, 25, selectedPartnerId, selectedChallengeId || undefined);
    } else {
      getAllVouchers(1, 25);
    }
    setPage(1);
    setSelectedIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPartnerId, selectedChallengeId]);

  const challengeVouchers: IVoucher[] = useMemo(
    () =>
      allVouchers.filter((voucher) => {
        if (voucher.sourceType === "CHALLENGE") return true;
        if (!voucher.sourceType && voucher.challenge) return true;
        return false;
      }),
    [allVouchers]
  );

  const selectedPartnerName =
    allPartners.find((p) => p._id === selectedPartnerId)?.name ??
    "All partners";

  const getChallengePartnerId = (challenge: any): string => {
    const rawPartner = challenge?.partnerId;
    if (typeof rawPartner === "string") return rawPartner;
    if (rawPartner && typeof rawPartner === "object") {
      return String(rawPartner._id ?? rawPartner.id ?? "");
    }
    return "";
  };

  const bulkPartnerChallenges = useMemo(() => {
    if (!bulkPartnerId) return [];
    return allPartnerChallenges.filter(
      (c: any) => getChallengePartnerId(c) === bulkPartnerId
    );
  }, [allPartnerChallenges, bulkPartnerId]);

  const filterPartnerChallenges = useMemo(() => {
    if (!selectedPartnerId) return [];
    return allPartnerChallenges.filter(
      (c: any) => getChallengePartnerId(c) === selectedPartnerId
    );
  }, [allPartnerChallenges, selectedPartnerId]);

  const selectedChallengeName = (() => {
    const challenge: any = filterPartnerChallenges.find(
      (c: any) => c._id === selectedChallengeId
    );
    return challenge ? challenge.en_title || challenge.nl_title : "";
  })();

  /** Bulk-edit selection: ids, kept across "Load more". */
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const loadedIds = challengeVouchers
    .map((voucher) => voucher._id as string)
    .filter(Boolean);
  const allLoadedSelected =
    loadedIds.length > 0 && loadedIds.every((id) => selectedSet.has(id));

  const toggleOne = (id: string) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleLoaded = () =>
    setSelectedIds((prev) =>
      allLoadedSelected
        ? prev.filter((id) => !loadedIds.includes(id))
        : [...prev, ...loadedIds.filter((id) => !prev.includes(id))]
    );

  // Selects every voucher of the partner (or of the picked challenge), also
  // the ones "Load more" has not fetched yet.
  const handleSelectAll = async () => {
    if (!selectedPartnerId) return;
    setIsSelectingAll(true);
    try {
      const selection = await getChallengeVoucherSelection(
        selectedPartnerId,
        selectedChallengeId || undefined
      );
      setSelectedIds(selection.ids);
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message: bulkErrorMessage(
          error,
          "Could not select all vouchers. Please try again."
        ),
        severity: "error",
      });
    } finally {
      setIsSelectingAll(false);
    }
  };

  const handleBulkEditDone = (result: IBulkVoucherResult) => {
    setIsBulkEditOpen(false);
    const notes = [
      `Changed ${result.changed} ${result.changed === 1 ? "voucher" : "vouchers"}.`,
      result.skippedIssued
        ? `${result.skippedIssued} already given to customers ${result.skippedIssued === 1 ? "was" : "were"} left as ${result.skippedIssued === 1 ? "it was" : "they were"}.`
        : "",
    ];
    setAlert({
      isOpen: true,
      message: notes.filter(Boolean).join(" "),
      severity: "success",
    });
    setSelectedIds([]);
    handleVoucherCreated();
  };

  const handleBulkDeleteDone = (result: IBulkDeleteResult) => {
    setIsBulkDeleteOpen(false);
    const notes = [
      `Deleted ${result.deleted} ${result.deleted === 1 ? "voucher" : "vouchers"}.`,
      result.skippedIssued
        ? `${result.skippedIssued} already given to customers ${result.skippedIssued === 1 ? "was" : "were"} kept.`
        : "",
      result.emptiedChallenges.length
        ? `${result.emptiedChallenges.join(", ")} now ${result.emptiedChallenges.length === 1 ? "has" : "have"} no vouchers: add the new ones now.`
        : "",
    ];
    setAlert({
      isOpen: true,
      message: notes.filter(Boolean).join(" "),
      severity: result.emptiedChallenges.length ? "warning" : "success",
    });
    setSelectedIds([]);
    handleVoucherCreated();
  };

  /** The counts for what the filters show: one challenge, or the whole partner. */
  const shownCounts: IVoucherCounts | null = summary
    ? selectedChallengeId
      ? summary.byChallenge.find(
          (row) => row.challenge === selectedChallengeId
        ) ?? { total: 0, issued: 0, products: 0, codes: 0 }
      : summary
    : null;

  const describeCounts = (counts: IVoucherCounts) => {
    const kinds =
      counts.products && counts.codes
        ? `${counts.products} product and ${counts.codes} with a code`
        : counts.products
          ? "all product vouchers"
          : counts.codes
            ? "all with a code"
            : "";
    const given = counts.issued
      ? `${counts.issued} given to customers`
      : "none given to customers";
    return `${counts.total} ${counts.total === 1 ? "voucher" : "vouchers"}${kinds ? ` (${kinds})` : ""}, ${given}.`;
  };

  const challengeCount = (challengeId: string) =>
    summary?.byChallenge.find((row) => row.challenge === challengeId)?.total ??
    0;

  const parseBooleanCell = (value: any): boolean | undefined => {
    if (value === undefined || value === null || value === "") return undefined;
    if (typeof value === "boolean") return value;
    const normalized = String(value).trim().toLowerCase();
    if (["true", "1", "yes", "y"].includes(normalized)) return true;
    if (["false", "0", "no", "n"].includes(normalized)) return false;
    return undefined;
  };

  const processBulkRows = async (rows: any[]) => {
    const onlyData = rows.slice(1);
    const payload = onlyData
      .filter(
        (row) =>
          Array.isArray(row) && row.length > 0 && String(row[0] ?? "").trim()
      )
      .map((row) => ({
        code: String(row[0]).trim(),
        value: row[1] ? String(row[1]) : undefined,
        description: row[2] ? String(row[2]) : undefined,
        instructions: row[3] ? String(row[3]) : undefined,
        benefitText: row[4] ? String(row[4]) : undefined,
        expireDate: row[5] ? String(row[5]) : undefined,
        allowMultipleRedemptions: parseBooleanCell(row[6]),
        partnerId: bulkPartnerId,
        challenge: bulkChallengeId,
        sourceType: "CHALLENGE" as const,
      }));

    if (!payload.length) {
      setAlert({
        isOpen: true,
        message: "No valid voucher rows found in file.",
        severity: "error",
      });
      return;
    }

    setIsBulkUploading(true);
    try {
      await addManyVoucher(payload as any);
      setAlert({
        isOpen: true,
        message: `Uploaded ${payload.length} challenge vouchers.`,
        severity: "success",
      });
      handleVoucherCreated();
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message:
          error?.response?.data?.message ??
          "Bulk upload failed. Please check your file format.",
        severity: "error",
      });
    } finally {
      setIsBulkUploading(false);
      setFileInputKey((prev) => prev + 1);
    }
  };

  const handleBulkFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!bulkPartnerId || !bulkChallengeId) {
      setAlert({
        isOpen: true,
        message: "Please select both partner and challenge before file upload.",
        severity: "error",
      });
      return;
    }

    const extension = file.name.split(".").pop()?.toLowerCase();
    if (extension === "csv") {
      Papa.parse(file, {
        complete: (result: any) => processBulkRows(result.data || []),
      });
      return;
    }

    if (extension === "xlsx" || extension === "xls") {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const allRows: any[] = [];
        workbook.SheetNames.forEach((sheetName) => {
          const worksheet = workbook.Sheets[sheetName];
          const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          allRows.push(...rows);
        });
        processBulkRows(allRows);
      };
      reader.readAsArrayBuffer(file);
      return;
    }

    setAlert({
      isOpen: true,
      message: "Unsupported file format. Use CSV or Excel.",
      severity: "error",
    });
  };

  const handleVoucherCreated = () => {
    if (selectedPartnerId) {
      getAllVouchers(1, 25, selectedPartnerId, selectedChallengeId || undefined);
    } else {
      getAllVouchers(1, 25);
    }
    setPage(1);
    loadSummary();
  };

  return (
    <div>
      <div className="flex flex-row items-center justify-between mb-6">
        <div>
          <h1 className="font-satoshi text-2xl font-bold text-dark">
            Challenge vouchers
          </h1>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            View and manage vouchers that are used as rewards for partner
            challenges. Start by selecting a partner to see their challenge
            vouchers.
          </p>
        </div>
        <PrimaryButton
          label="Create voucher"
          onClick={() => setIsModalOpen(true)}
          className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
        />
      </div>

      <ChallengeVoucherModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        partnerId={selectedPartnerId}
        partnerName={selectedPartnerName}
        onCreated={handleVoucherCreated}
      />

      <BulkEditVouchersModal
        open={isBulkEditOpen}
        onClose={() => setIsBulkEditOpen(false)}
        ids={selectedIds}
        sample={challengeVouchers.find((voucher) =>
          selectedSet.has(voucher._id as string)
        )}
        onDone={handleBulkEditDone}
      />

      <BulkDeleteVouchersModal
        open={isBulkDeleteOpen}
        onClose={() => setIsBulkDeleteOpen(false)}
        ids={selectedIds}
        onDone={handleBulkDeleteDone}
      />

      <div className="bg-white rounded-xl shadow-none drop-shadow-none border-gray-border mb-4">
        {true && (
          <div className="w-full border-b border-gray-border border-l-4 border-l-[#FF6E41] px-6 py-4">
            <p className="font-satoshi font-medium text-base text-dark">
              Add many (challenge vouchers)
            </p>
            <p className="text-xs text-gray-600 mt-1 mb-3">
              Select partner and challenge first, then upload CSV/Excel. Format:
              Code, Value, Description, Instructions, Benefit Text, Expire Date
              (dd-mm-yyyy), Allow Multiple Redeems (true/false).
            </p>
            <a
              href="/voucher-upload-template.csv"
              download="voucher-upload-template.csv"
              className="inline-flex items-center mb-3 rounded-md border border-[#FF6E41] bg-[#FF6E41]/10 px-3 py-1.5 text-xs font-satoshi font-semibold text-[#FF6E41] hover:bg-[#FF6E41]/15 transition-colors"
            >
              Download example CSV
            </a>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Partner *
                </label>
                <FormSelect
                  value={bulkPartnerId}
                  onChange={(e: any) => {
                    setBulkPartnerId(e.target.value);
                    setBulkChallengeId("");
                  }}
                  options={[
                    { value: "", label: "Select partner..." },
                    ...allPartners.map((partner) => ({
                      value: partner._id as string,
                      label: partner.name as string,
                    })),
                  ]}
                  placeholder="Select partner..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Challenge *
                </label>
                <FormSelect
                  value={bulkChallengeId}
                  onChange={(e: any) => setBulkChallengeId(e.target.value)}
                  options={[
                    { value: "", label: "Select challenge..." },
                    ...bulkPartnerChallenges.map((challenge: any) => ({
                      value: challenge._id,
                      label: challenge.en_title || challenge.nl_title,
                    })),
                  ]}
                  placeholder="Select challenge..."
                />
              </div>
              <div className="flex justify-start sm:justify-end">
                <input
                  key={fileInputKey}
                  id="bulk-challenge-vouchers-input"
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  onChange={handleBulkFileUpload}
                />
                <PrimaryButton
                  label={isBulkUploading ? "Uploading..." : "Add many"}
                  onClick={() => {
                    (
                      document.getElementById(
                        "bulk-challenge-vouchers-input"
                      ) as HTMLInputElement | null
                    )?.click();
                  }}
                  disabled={
                    isBulkUploading || !bulkPartnerId || !bulkChallengeId
                  }
                />
              </div>
            </div>
          </div>
        )}
        <div className="w-full border-b border-gray-border px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="font-satoshi font-medium text-base text-dark">
              Partner selection
            </p>
            <p className="text-xs text-gray-600 mt-1">
              Choose a partner to filter vouchers, or leave it empty to see all
              challenge vouchers.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="min-w-[220px]">
              <FormSelect
                value={selectedPartnerId}
                onChange={(e: any) => {
                  setSelectedPartnerId(e.target.value);
                  setSelectedChallengeId("");
                }}
                options={[
                  { value: "", label: "All partners" },
                  ...allPartners.map((partner) => ({
                    value: partner._id as string,
                    label: partner.name as string,
                  })),
                ]}
                placeholder="All partners"
              />
            </div>
            {selectedPartnerId && (
              <div className="min-w-[220px]" data-testid="challenge-filter">
                <FormSelect
                  value={selectedChallengeId}
                  onChange={(e: any) => setSelectedChallengeId(e.target.value)}
                  options={[
                    {
                      value: "",
                      label: summary
                        ? `All challenges (${summary.total})`
                        : "All challenges",
                    },
                    ...filterPartnerChallenges.map((challenge: any) => ({
                      value: challenge._id,
                      label: `${challenge.en_title || challenge.nl_title}${
                        summary ? ` (${challengeCount(challenge._id)})` : ""
                      }`,
                    })),
                  ]}
                  placeholder="All challenges"
                />
              </div>
            )}
          </div>
        </div>

        {canBulkEdit && (
          <div
            className="w-full border-b border-gray-border px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            data-testid="bulk-toolbar"
          >
            <div className="text-sm text-gray-700 space-y-1 flex-1 min-w-0">
              {shownCounts && selectedPartnerId ? (
                <p data-testid="voucher-counts">
                  <strong>{selectedChallengeName || selectedPartnerName}:</strong>{" "}
                  {describeCounts(shownCounts)}
                  {shownCounts.products && shownCounts.codes
                    ? " Pick one challenge to change their text: product and code vouchers cannot be edited together."
                    : ""}
                </p>
              ) : null}
              <p>
                {selectedIds.length ? (
                  <>
                    <strong>{selectedIds.length}</strong>{" "}
                    {selectedIds.length === 1 ? "voucher" : "vouchers"} selected.{" "}
                    <button
                      type="button"
                      className="underline text-gray-600"
                      onClick={() => setSelectedIds([])}
                    >
                      Clear selection
                    </button>
                  </>
                ) : selectedPartnerId ? (
                  "Tick vouchers to edit or delete them together, or select them all."
                ) : (
                  "Tick vouchers to edit or delete them together. Pick a partner to select all its vouchers at once."
                )}
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {selectedPartnerId && (
                <button
                  type="button"
                  className="max-w-[280px] text-right text-sm font-satoshi font-semibold text-[#FF6E41] underline disabled:opacity-50"
                  onClick={handleSelectAll}
                  disabled={isSelectingAll}
                >
                  {isSelectingAll
                    ? "Selecting..."
                    : `Select all vouchers of ${selectedChallengeName || selectedPartnerName}`}
                </button>
              )}
              <PrimaryButton
                label="Edit selected"
                size="sm"
                onClick={() => setIsBulkEditOpen(true)}
                disabled={!selectedIds.length}
                className="whitespace-nowrap !bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
              />
              <button
                type="button"
                className="whitespace-nowrap bg-red-400 hover:opacity-90 active:bg-red-300 transition ease-linear rounded-[5px] px-4 py-1 font-satoshi font-medium text-white text-sm disabled:opacity-50 disabled:pointer-events-none"
                onClick={() => setIsBulkDeleteOpen(true)}
                disabled={!selectedIds.length}
              >
                Delete selected
              </button>
            </div>
          </div>
        )}

        <div className="w-full px-6 py-4">
          {!challengeVouchers.length ? (
            <p className="text-sm text-gray-600">
              {selectedPartnerId ? (
                <>
                  There are no challenge vouchers for{" "}
                  <strong>{selectedPartnerName}</strong> yet.
                </>
              ) : (
                "There are no challenge vouchers yet."
              )}
            </p>
          ) : (
            <div className="relative overflow-x-auto">
              <table className="w-full text-base font-normal font-satoshi text-left rtl:text-right text-dark">
                <thead className="font-medium bg-gray-50">
                  <tr>
                    {canBulkEdit && (
                      <th className="px-2 sm:px-4 py-2 sm:py-3 w-8">
                        <input
                          type="checkbox"
                          aria-label="Select all vouchers shown"
                          checked={allLoadedSelected}
                          onChange={toggleLoaded}
                        />
                      </th>
                    )}
                    <th className="px-2 sm:px-6 py-2 sm:py-3">Code</th>
                    <th className="px-2 sm:px-6 py-2 sm:py-3">Prize name</th>
                    <th className="px-2 sm:px-6 py-2 sm:py-3">Partner</th>
                    <th className="px-2 sm:px-6 py-2 sm:py-3">Challenge</th>
                    <th className="px-2 sm:px-6 py-2 sm:py-3">Value</th>
                    <th className="hidden sm:table-cell px-2 sm:px-6 py-2 sm:py-3">
                      Instructions
                    </th>
                    <th className="hidden sm:table-cell px-2 sm:px-6 py-2 sm:py-3">
                      Benefit text
                    </th>
                    <th className="hidden sm:table-cell px-2 sm:px-6 py-2 sm:py-3">
                      Expire date
                    </th>
                    <th className="hidden sm:table-cell px-2 sm:px-6 py-2 sm:py-3">
                      Status
                    </th>
                    <th className="px-2 sm:px-6 py-2 sm:py-3 text-right">
                      Redeemed
                    </th>
                    <th className="px-2 sm:px-6 py-2 sm:py-3 text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody ref={animationParent}>
                  {challengeVouchers.map((voucher) => (
                    <tr
                      key={voucher._id}
                      onClick={() =>
                        voucher._id &&
                        navigate(`/voucher-details/${voucher._id}`)
                      }
                      className="bg-white hover:bg-gray-100 transition ease-linear border-b cursor-pointer"
                    >
                      {canBulkEdit && (
                        <td
                          className="px-2 sm:px-4 py-2 sm:py-3"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            aria-label="Select voucher"
                            checked={selectedSet.has(voucher._id as string)}
                            onChange={() => toggleOne(voucher._id as string)}
                          />
                        </td>
                      )}
                      <td className="px-2 sm:px-6 py-2 sm:py-3 break-all sm:whitespace-nowrap">
                        {voucher.code}
                      </td>
                      <td className="px-2 sm:px-6 py-2 sm:py-3 max-w-xs truncate">
                        {voucher.prizeName || "-"}
                      </td>
                      <td className="px-2 sm:px-6 py-2 sm:py-3 whitespace-nowrap">
                        {typeof voucher.partnerId === "object" &&
                        (voucher.partnerId as any)
                          ? ((voucher.partnerId as any)?.name ?? "-")
                          : "-"}
                      </td>
                      <td className="px-2 sm:px-6 py-2 sm:py-3">
                        {typeof voucher.challenge === "object" &&
                        voucher.challenge
                          ? // @ts-ignore
                            voucher.challenge?.title ||
                            // @ts-ignore
                            voucher.challenge?.en_title ||
                            // @ts-ignore
                            voucher.challenge?.nl_title ||
                            // @ts-ignore
                            voucher.challenge?.name ||
                            // @ts-ignore
                            voucher.challenge?._id ||
                            "-"
                          : voucher.challenge
                            ? String(voucher.challenge)
                            : "-"}
                      </td>
                      <td className="px-2 sm:px-6 py-2 sm:py-3">
                        {voucher.value || "No value"}
                      </td>
                      <td className="hidden sm:table-cell px-2 sm:px-6 py-2 sm:py-3 max-w-xs truncate">
                        {voucher.instructions || "No instructions"}
                      </td>
                      <td className="hidden sm:table-cell px-2 sm:px-6 py-2 sm:py-3 max-w-xs truncate">
                        {voucher.benefitText || "No benefit text"}
                      </td>
                      <td className="hidden sm:table-cell px-2 sm:px-6 py-2 sm:py-3">
                        {voucher.expireDate
                          ? dayjs(voucher.expireDate).format("DD/MM/YYYY")
                          : "No expire date"}
                      </td>
                      <td className="hidden sm:table-cell px-2 sm:px-6 py-2 sm:py-3">
                        {voucher.status}
                      </td>
                      <td
                        className={`px-2 sm:px-6 py-2 sm:py-3 text-right ${
                          Array.isArray(voucher.redeemedBy) &&
                          voucher.redeemedBy.length > 0
                            ? "text-green-500"
                            : "text-red-500"
                        }`}
                      >
                        {Array.isArray(voucher.redeemedBy) &&
                        voucher.redeemedBy.length > 0
                          ? voucher.redeemedBy.length
                          : "Not redeemed"}
                      </td>
                      <td className="px-2 sm:px-6 py-2 sm:py-3 text-right">
                        <TrashIcon
                          className="inline-block cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            const confirmDelete = window.confirm(
                              "Are you sure you want to delete this Voucher?"
                            );
                            if (confirmDelete && voucher._id) {
                              deleteVoucher(voucher._id as string);
                            }
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {hasNextPage && (
            <div className="flex justify-end mt-4">
              <PrimaryButton
                label="Load more"
                size="sm"
                onClick={loadMore}
                className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  function loadMore() {
    if (!hasNextPage) return;
    const nextPage = page + 1;
    setPage(nextPage);
    if (selectedPartnerId) {
      getAllVouchers(
        nextPage,
        25,
        selectedPartnerId,
        selectedChallengeId || undefined
      );
    } else {
      getAllVouchers(nextPage, 25);
    }
  }
}

export default withProtectedRoute(ChallengeVouchersScreen);
